import { useEffect, useState } from 'react';
import { getCreditApplicationTypeLabel } from '@dill/dill-shared';
import { getCreditApplications } from '../../../../../redux/services/creditApplicationsService';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { getSupplierBranches } from '../../../../../redux/services/supplierAdditionalDetailsService';
import { formatBranchLabel } from '../../../../../utils/helpers';
import { CreditAppDataRow } from './types';

export const useCreditAppsData = () => {
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<CreditAppDataRow[]>([]);

  const creditApplications = useAppSelector((state) => state.creditApplications.creditApplications);
  const loadingList = useAppSelector((state) => state.creditApplications.loadingList);
  const { creditApplicationTemplates } = useAppSelector(
    (state) => state.creditApplicationTemplates
  );
  const { user, users, firebaseUserId } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);

  const isLoading = loadingList.includes('getCreditApplications');
  const mainRoles = user?.roles.filter((role) => role !== 'DILL_ADMIN');

  useEffect(() => {
    if (mainRoles?.includes('SUPPLIER') && firebaseUserId && currentSupplier?.id) {
      dispatch(
        getCreditApplications({
          supplierId: currentSupplier.id,
        })
      );
    }
  }, [user?.id, firebaseUserId, currentSupplier?.id]);

  useEffect(() => {
    if (!creditApplications || creditApplications.length === 0) {
      setRows([]);
      return;
    }

    const branches = supplierBranches;
    const branchMap = new Map(
      branches.map((b) => [b.branchId, formatBranchLabel(b) || b.branchId])
    );

    // Build user map for reviewer resolution
    const userMap = new Map(users.map((u) => [u.id, `${u.firstName} ${u.lastName}`.trim()]));

    // Build sales rep map from branches
    const salesRepMap = new Map<string, string>();
    branches.forEach((branch) => {
      (branch.salesRep ?? []).forEach((rep) => {
        if (rep?.id) {
          salesRepMap.set(rep.id, rep.name || '');
        }
      });
    });

    const flattenedRows: CreditAppDataRow[] = creditApplications
      .filter((app) => !app.isDeleted)
      .map((app) => {
        // Extract contact name from fieldValues (firstName + lastName at stepNumber 0)
        const firstName =
          (app.fieldValues.find((f) => f.id.includes('first') && f.stepNumber === 0)
            ?.value as string) ?? '';
        const lastName =
          (app.fieldValues.find((f) => f.id.includes('last') && f.stepNumber === 0)
            ?.value as string) ?? '';
        const contact = `${firstName} ${lastName}`.trim();

        // Extract company name from fieldValues
        const company = (
          (app.fieldValues.find((f) => f.id === 'customer-or-company-name')?.value as string) ?? ''
        ).toLowerCase();

        // Extract credit amount from fieldValues
        let creditAmount = 0;
        if (app.applicationType === 'CASH_APPLICATION') {
          // Check if it was later approved as COD with an amount
          if (app.approvalStatusHistory && app.approvalStatusHistory.length > 0) {
            const sortedHistory = [...app.approvalStatusHistory].sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
            );
            const latestStatus = sortedHistory[0].status;
            if (latestStatus === 'APPROVED_COD' || latestStatus === 'INTERNALLY_APPROVED_COD') {
              const rawAmount = app.fieldValues.find((f) => f.id.includes('amount-of'))?.value;
              creditAmount = rawAmount ? Number(rawAmount) || 0 : 0;
            }
          }
          // Otherwise stays 0 for CASH_APPLICATION
        } else {
          const rawAmount = app.fieldValues.find((f) => f.id.includes('amount-of'))?.value;
          creditAmount = rawAmount ? Number(rawAmount) || 0 : 0;
        }

        // Approved amount
        const approvedAmount = app.amountAproved ?? 0;

        const approvedAt = app.approvedAt
          ? new Date(app.approvedAt).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            })
          : '';

        // Resolve branch name
        const branchName = app.branchId ? branchMap.get(app.branchId) || '' : '';

        // Resolve reviewer name
        const reviewer = app.assignedReviewerUserId
          ? userMap.get(app.assignedReviewerUserId) || ''
          : '';

        // Resolve sales rep names
        const salesRepNames = (app.assignedSalesRep ?? [])
          .map((repId) => salesRepMap.get(repId) || '')
          .filter(Boolean);
        const salesRep = salesRepNames.join(', ');

        // Format created date
        const createdAtStr = app.createdAt
          ? new Date(app.createdAt).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            })
          : '';

        // Format status display - convert snake case to title case
        const statusDisplay = (app.status || '')
          .replace(/_/g, ' ')
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');

        // Application type display (an external form shows its template's name, when loaded)
        const applicationTypeDisplay = getCreditApplicationTypeLabel(app.applicationType, {
          long: true,
          formName: creditApplicationTemplates.find((t) => t.id === app.templateId)?.name,
        });

        return {
          id: app.id,
          contact,
          email: app.senderEmail || '',
          company,
          createdAt: createdAtStr,
          requestedBy: (app.requestedBy || '').toLowerCase(),
          creditAmount,
          approvedAmount,
          approvedAt,
          applicationType: applicationTypeDisplay,
          status: statusDisplay,
          reviewer,
          branch: branchName,
          salesRep,
        };
      });

    setRows(flattenedRows);
  }, [creditApplications, creditApplicationTemplates, currentSupplier, users, supplierBranches]);

  return { rows, isLoading };
};
